import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Image } from '@codes/ui-kit/components/ui/image';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { CloudStereo, ProjectBody, ProjectView, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { useWorkspaceSettingDialog } from '@web/app/features/home/hooks/useWorkspaceSettingDialog';
import { Check, ChevronLeft, ChevronRight, Info } from 'lucide-react';

import { AWSImage, Azure, GoogleCloudImage } from '@codes/assets';
import { useRegions } from '@codes/cloud';
import { useCreateProject } from '@codes/project';
import { PrivacyTermsPage } from '@codes/shared';
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
    Input,
    Label,
    PageDialog,
    PageDialogContent,
    PageDialogDescription,
    PageDialogFooter,
    PageDialogHeader,
    PageDialogTitle,
} from '@codes/ui-kit';

import { useDialogStore } from '../../stores';

interface CreateProjectDialogProps {
    handleClose: () => void;
    handleComplete: (project: ProjectView) => void;
    workspace: WorkspaceView;
}

export const textRegex = /^[\s\S]{3,256}$/;

export function CreateProjectDialog({ workspace, handleClose, handleComplete }: CreateProjectDialogProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        mutateAsync: createProject,
        isPending: isCreating,
        data: createdProject,
    } = useCreateProject({
        duplicatedErrorMsg: t('project.create.duplicatedErrorMsgText'),
    });
    const { isProjectDialogOpen, setProjectDialog } = useDialogStore(state => state);

    const [isCloseProjectDialogOpen, setCloseDialogOpen] = useState(false);

    const { data: regions } = useRegions({ limit: 100, cloudId: workspace?.cloudId });

    const projectForm = useForm<ProjectBody>({
        defaultValues: {
            name: '',
            stereo: '',
            regionId: '',
        },
    });

    const [watchedName, watchedStage, watchedRegionId] = useWatch({
        control: projectForm.control,
        name: ['name', 'stereo', 'regionId'],
    });

    const haveRequiredInfos =
        ((!!workspace?.secret$ && !!workspace?.secret$.accountNo) || workspace?.stereo === 'basic') &&
        !!workspace.regionId &&
        !!workspace.region$;

    const getCloudImage = (stereo: CloudStereo | undefined) => {
        switch (stereo) {
            case 'aws':
                return AWSImage;
            case 'azure':
                return Azure;
            case 'gcp':
                return GoogleCloudImage;
            default:
                return GoogleCloudImage;
        }
    };

    const onSubmit = () => {
        createProject({
            name: watchedName?.trim(),
            workspaceId: workspace.id,
            regionId: watchedRegionId,
            cloudId: workspace.cloudId,
            stereo: watchedStage as 'dev' | 'prod',
        });
    };

    const handleCompleteToCreateProject = (newProject: ProjectView) => {
        handleComplete(newProject);
    };

    const resetData = () => {
        projectForm.reset();

        if (workspace) {
            projectForm.setValue('name', `${workspace.name} cloud project`);
            projectForm.setValue('regionId', workspace.regionId);
        }
    };

    const { open: openWorkspaceSettingDialog } = useWorkspaceSettingDialog();

    const handleSettingBtnClick = () => {
        if (!workspace.id) {
            return;
        }

        openWorkspaceSettingDialog(workspace.id as string);
    };

    const closeButtonTapped = () => {
        const hasFields = !!watchedName || !!watchedRegionId || !!watchedStage;

        if (hasFields) {
            setCloseDialogOpen(true);
            return;
        }

        setProjectDialog(false);
    };

    const [termsAgreed, setTermsAgreed] = useState(false);

    useEffect(() => {
        if (!createdProject) {
            return;
        }

        handleCompleteToCreateProject(createdProject);
    }, [createdProject]);

    useEffect(() => {
        if (isProjectDialogOpen) {
            resetData();
        }
    }, [isProjectDialogOpen]);

    return (
        <PageDialog open={isProjectDialogOpen} onOpenChange={setProjectDialog}>
            <PageDialogContent onInteractOutside={e => e.preventDefault()} aria-describedby={undefined}>
                <div className="w-full max-w-[500px] sm:pt-[56px] sm:pb-[34px]">
                    <PageDialogHeader>
                        <PageDialogTitle>{t('project.create.createProject')}</PageDialogTitle>
                        <AlertDialog open={isCloseProjectDialogOpen} onOpenChange={setCloseDialogOpen}>
                            <button
                                className="absolute left-6 top-[19px] z-[51]"
                                type="button"
                                onClick={closeButtonTapped}
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <AlertDialogContent>
                                <AlertDialogTitle>{t('project.create.confirmStopProjectCreation')}</AlertDialogTitle>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>{t('project.create.goBack')}</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleClose}>
                                        {t('project.create.stop')}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </PageDialogHeader>
                    <PageDialogDescription className="text-lg mt-5 mb-8 font-medium text-text px-6">
                        {t('project.create.enterInfoToCreateProject')}
                    </PageDialogDescription>
                    {!haveRequiredInfos ? (
                        <div className="mt-6  pb-6 border-detail-line px-6">
                            <Label variant="form" className="flex items-center gap-1 text-point">
                                <Info className="w-[14px] h-[14px]" />
                                {t('project.create.alertEnterInfo')}
                            </Label>
                            <button
                                onClick={handleSettingBtnClick}
                                className="w-full h-[52px] px-4 rounded-lg bg-box border flex items-center justify-between border-text-input-border"
                            >
                                <div className="text-sm flex items-center gap-1.5 font-medium  text">
                                    <Check className="w-4 h-4" />
                                    {t('project.create.goToSettings')}
                                </div>
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={projectForm.handleSubmit(onSubmit)}>
                            <div className="pb-5 overflow-auto px-6 space-y-[22px]">
                                <div>
                                    <Label variant="form">{t('project.create.selectedCloud')}</Label>
                                    <div className="rounded-lg !border-[0.6px] border-gradient py-2 flex  justify-center items-center gap-3">
                                        <Image
                                            src={getCloudImage(workspace?.cloud$?.stereo)}
                                            alt="im image"
                                            className="w-6"
                                        />
                                        <span className="text-sm text-center font-semibold">
                                            {workspace?.cloud$?.name}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <Label variant="form">{t('project.create.projectName')}</Label>
                                    <Controller
                                        name="name"
                                        rules={{
                                            required: t('project.create.nameRequiredErrorText'),
                                            validate: value => {
                                                const trimmed = value?.trim() || '';
                                                if (!textRegex.test(trimmed)) {
                                                    return t('project.create.nameValidErrorText');
                                                }
                                                return true;
                                            },
                                        }}
                                        control={projectForm.control}
                                        render={({ field, fieldState }) => (
                                            <div className="w-full">
                                                <Input {...field} placeholder={t('project.create.placeholder')} />
                                                {fieldState.error && (
                                                    <p className="mt-1 text-xs text-red-500">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                                <div>
                                    <Label variant="form">Select Region</Label>
                                    <Controller
                                        name="regionId"
                                        rules={{
                                            required: t('project.create.regionRequiredErrorText'),
                                        }}
                                        control={projectForm.control}
                                        render={({ field, fieldState }) => (
                                            <div className="w-full">
                                                <Select
                                                    value={field.value}
                                                    onValueChange={regionId => {
                                                        field.onChange(regionId);
                                                    }}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {regions?.list.map(region => (
                                                            <SelectItem key={region.id} value={region.id as string}>
                                                                {region.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {fieldState.error && (
                                                    <p className="mt-1 text-xs text-red-500">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                                <div>
                                    <Label variant="form">Select Stage</Label>
                                    <Controller
                                        name="stereo"
                                        rules={{
                                            required: t('project.create.stereoRequiredErrorText'),
                                        }}
                                        control={projectForm.control}
                                        render={({ field, fieldState }) => (
                                            <div className="w-full">
                                                <Select
                                                    value={field.value}
                                                    onValueChange={stage => {
                                                        field.onChange(stage);
                                                    }}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select a stage" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="dev">
                                                            {t('project.create.development')}
                                                        </SelectItem>
                                                        <SelectItem value="prod">
                                                            {t('project.create.production')}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                {fieldState.error && (
                                                    <p className="mt-1 ml-[6px] text-xs text-error font-medium">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* 약관동의 */}
                                <div className="flex items-center justify-between mt-[30px]">
                                    <button
                                        type="button"
                                        className="flex items-center gap-1.5"
                                        onClick={() => setTermsAgreed(!termsAgreed)}
                                        disabled={isCreating}
                                    >
                                        <div
                                            className={`w-4 h-4 border-[1.5px] rounded-full flex items-center justify-center ${
                                                termsAgreed ? 'border-primary' : 'border-border'
                                            }`}
                                        >
                                            {termsAgreed && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                        </div>
                                        <div className="text-sm text-sub font-medium cursor-pointer">
                                            {t('workspace.createV2.termsAgreement')}
                                        </div>
                                    </button>

                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <button
                                                className="text-sm text-sub underline hover:text-text"
                                                disabled={isCreating}
                                            >
                                                {t('workspace.createV2.termsView')}
                                            </button>
                                        </DialogTrigger>
                                        <DialogPortal>
                                            <DialogContent className="max-md:max-w-full max-md:w-full max-md:max-h-[100vh] max-w-[800px] max-md:rounded-none p-0">
                                                <DialogHeader>
                                                    <DialogTitle>Privacy & Terms</DialogTitle>
                                                </DialogHeader>
                                                <PrivacyTermsPage isDialog={true} />
                                            </DialogContent>
                                        </DialogPortal>
                                    </Dialog>
                                </div>
                            </div>
                            <PageDialogFooter className="mt-10">
                                <Button size="lg" className="w-full" disabled={isCreating || !termsAgreed}>
                                    {t(isCreating ? 'project.create.loading' : 'project.create.complete')}
                                </Button>
                            </PageDialogFooter>
                        </form>
                    )}
                </div>
            </PageDialogContent>
        </PageDialog>
    );
}
